export const DOMAIN_COLORS = [
  "#367fc0",
  "#b57748",
  "#298a7c",
  "#9270aa",
  "#c06883",
  "#7b8b46",
];
const DOMAIN_LABELS = {
  "mentor-table-and-blue-ribbon-qna": "Mentor sessions & Blue Ribbon Q&A",
  "paid-traffic-cac-response": "Paid traffic & acquisition",
  "lifecycle-email-sms": "Email & SMS lifecycle",
  "business-systems-operations": "Business systems & operations",
  "unclassified-other": "Other topics",
  "core-growth-operating-system": "Growth strategy",
  "creative-testing-and-copy": "Creative testing & copy",
  "product-creation-launch": "Product creation & launches",
  "podcast-show-public-context": "Podcasts & conversations",
  "ai-marketing-hub": "AI marketing",
  "cro-aov-product-page": "Conversion & product pages",
};
export const ROOT = "agent";
export const clampZoom = (k) => Math.min(3.6, Math.max(0.2, k));
export function zoomAt(camera, factor, point) {
  const k = clampZoom(camera.k * factor);
  return {
    x: point.x - ((point.x - camera.x) * k) / camera.k,
    y: point.y - ((point.y - camera.y) * k) / camera.k,
    k,
  };
}
export function buildAtlas(
  collections,
  catalog,
  agentName,
  catalogSources = [],
) {
  const nodes = [
    {
      id: ROOT,
      title: agentName,
      kind: "agent",
      color: "#153958",
      x: 0,
      y: 0,
      depth: 0,
      parent: null,
    },
  ];
  const edges = [];
  const add = (row, parent) => {
    if (nodes.some((n) => n.id === row.id)) return;
    nodes.push({
      ...row,
      parent,
      depth: (nodes.find((n) => n.id === parent)?.depth || 0) + 1,
    });
    edges.push({ from: parent, to: row.id, relation: "part of" });
  };
  const sharedEntries =
    collections.find((c) => c.id === "shared")?.entries || [];
  const operating = sharedEntries.filter((e) =>
    /xscale operating/i.test(e.sourceLabel),
  );
  const sections = collections.map((c) =>
    c.id === "shared"
      ? { ...c, entries: c.entries.filter((e) => !operating.includes(e)) }
      : c,
  );
  if (operating.length)
    sections.push({
      id: "operating",
      title: "XScale guidance",
      color: "#7a8799",
      scope: "Shared operating knowledge",
      icon: "skill",
      entries: operating.map((e) => ({ ...e, collectionId: "operating" })),
    });
  sections.forEach((c, i) => {
    const angle = -Math.PI / 2 + (i * Math.PI * 2) / sections.length;
    add(
      {
        ...c,
        kind: "collection",
        count:
          c.id === "shared" && catalog?.accessGranted
            ? catalog.packages.reduce((sum, p) => sum + p.sourceCount, 0)
            : c.entries.length,
        x: Math.cos(angle) * 710,
        y: Math.sin(angle) * 530,
      },
      ROOT,
    );
    if (c.id === "shared" && catalog?.accessGranted) {
      if (catalog.playbooks?.length) {
        add(
          {
            id: "playbooks",
            title: "Offers & funnels",
            summary: "Methods, examples and applications",
            kind: "group",
            color: "#b57748",
            count: catalog.playbooks.length,
            collectionId: c.id,
          },
          c.id,
        );
        for (const b of catalog.playbooks) {
          add(
            {
              ...b,
              id: `playbook:${b.id}`,
              recordId: b.id,
              kind: "playbook",
              collectionId: c.id,
              color: b.color || "#b57748",
              sourceLabel: "Smart Marketer · Playbook",
            },
            "playbooks",
          );
          for (const [section, title, label] of [
            ["method", "The method", "Step-by-step guidance"],
            ["example", "Worked example", "See the method in practice"],
            ["brand", "Your brand application", "Proposed assignment"],
            ["sources", "Source lesson", "Supporting evidence"],
          ])
            add(
              {
                id: `section:${b.id}:${section}`,
                bookId: b.id,
                section,
                title,
                kind: "playbookSection",
                color: b.color || "#b57748",
                sourceLabel: label,
                collectionId: c.id,
              },
              `playbook:${b.id}`,
            );
        }
      }
      for (const [j, d] of (catalog.domains || []).entries()) {
        add(
          {
            ...d,
            id: `domain:${d.id}`,
            title: DOMAIN_LABELS[d.id] || d.title,
            recordId: d.id,
            kind: "domain",
            color: DOMAIN_COLORS[j % DOMAIN_COLORS.length],
            count: d.sourceCount,
            collectionId: c.id,
            sourceLabel: "Source library",
          },
          c.id,
        );
      }
    }
    // Runtime categories are genuine source metadata. No inferred learning links.
    const groups = [...new Set(c.entries.map((e) => e.category || "Sources"))];
    for (const e of c.entries) {
      if (
        c.id === "shared" &&
        catalog?.playbooks?.some(
          (b) => b.title.toLowerCase() === e.title.toLowerCase(),
        )
      )
        continue;
      const grouped = c.entries.length > 8 && groups.length > 1;
      const groupId = `group:${c.id}:${e.category || "Sources"}`;
      if (grouped)
        add(
          {
            id: groupId,
            title: e.category || "Sources",
            kind: "group",
            color: c.color,
            collectionId: c.id,
            count: c.entries.filter((r) => r.category === e.category).length,
          },
          c.id,
        );
      add(
        { ...e, kind: e.kind, nodeType: "entry", color: c.color },
        grouped ? groupId : c.id,
      );
    }
  });
  for (const s of catalogSources) {
    const parent = `domain:${s.domainId}`;
    if (nodes.some((n) => n.id === parent))
      add(
        {
          ...s,
          id: `catalog:${s.id}`,
          recordId: s.id,
          kind: "catalog",
          color: nodes.find((n) => n.id === parent).color,
          sourceLabel: s.course,
          summary: `${s.passageCount} indexed passages · ${s.course}`,
          collectionId: "shared",
        },
        parent,
      );
  }
  // Deterministic nested coordinates keep a node in the same place as you travel.
  for (const parent of [...nodes]) {
    if (parent.id === ROOT) continue;
    const children = nodes.filter((n) => n.parent === parent.id);
    const wide = children.length > 8;
    const rx = wide ? 880 : parent.depth === 1 ? 620 : 480;
    const ry = wide ? 420 : parent.depth === 1 ? 360 : 310;
    children.forEach((child, i) => {
      if (children.length > 16) {
        // Large source libraries extend into a spacious, pannable field.
        // Reserve the center for the focused topic; never fit 100 labels at once.
        const row = Math.floor(i / 8);
        child.x = parent.x + ((i % 8) - 3.5) * 310;
        child.y =
          parent.y + (row % 2 ? -1 : 1) * (300 + Math.floor(row / 2) * 210);
      } else {
        const angle =
          -Math.PI / 2 + ((i + 0.5) * Math.PI * 2) / children.length;
        child.x = parent.x + Math.cos(angle) * rx;
        child.y = parent.y + Math.sin(angle) * ry;
      }
    });
  }
  for (const b of catalog?.playbooks || [])
    for (const id of b.sourceIds || []) {
      if (nodes.some((n) => n.id === `catalog:${id}`))
        edges.push({
          from: `playbook:${b.id}`,
          to: `catalog:${id}`,
          relation: "supported by",
        });
    }
  return { nodes, edges, sections };
}
export function ancestry(nodes, id) {
  const path = [];
  let node = nodes.find((n) => n.id === id);
  const visited = new Set();
  while (node && !visited.has(node.id)) {
    visited.add(node.id);
    path.unshift(node);
    node = nodes.find((n) => n.id === node.parent);
  }
  return path;
}
export function neighborhood(nodes, focusId) {
  const focus = nodes.find((n) => n.id === focusId) || nodes[0];
  const children = nodes.filter((n) => n.parent === focus.id);
  return {
    focus,
    children,
    visible: new Set([
      focus.id,
      focus.parent,
      ...children.map((n) => n.id),
      ...nodes
        .filter((n) => n.parent === focus.parent && focus.depth > 1)
        .map((n) => n.id),
    ]),
  };
}
